import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "react-query";
import { fetchActionsUtil, sendFormData } from "../../utils/helpers";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import { DeleteIcon } from "../Icons";
import _ from "lodash";
import Datetime from "react-datetime";

const validationSchema = Yup.object().shape({
  enrollment_id: Yup.number().required("Enrollment is required"),

  participants: Yup.array().of(
    Yup.object().shape({
      participant_id: Yup.number().required("Participant is required"),
      certificate_number: Yup.string().required(
        "Certificate number is required",
      ),
      issued_date: Yup.date().required("Issued date is required"),
      expiry_date: Yup.date().nullable(),
      pdf_file: Yup.mixed().nullable(),
      verification_code: Yup.string(),
      course_enrollment_status: Yup.boolean(),
    }),
  ),
});

function UploadCertificateModal({
  show,
  onHide,
  onSuccess,
  mode = "create",
  certificate = null,
  refetch,
  enrollmentId = null,
}) {
  const { backendUrl, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // Fetch enrollments from server
  const fetchEnrollments = async (inputValue, callback) => {
    try {
      let query = "?limit=50";
      if (inputValue) {
        query += `&searchQuery=${inputValue}`;
      }
      const data = await fetchActionsUtil(
        `${backendUrl}/api/partnership/course-enrollments${query}`,
        "GET",
        token,
      );
      const enrollmentOptions = (data.data || data || []).map((enrollment) => ({
        value: enrollment.id,
        label: `${enrollment?.course?.title || enrollment.course_id} - ${
          enrollment.full_name
        } (${enrollment.email})`,
        enrollment,
      }));

      callback(enrollmentOptions);
    } catch (err) {
      console.log(err);
      callback([]);
    }
  };

  const debouncedFetchEnrollments = _.debounce(fetchEnrollments, 500);

  const loadEnrollmentOptions = (inputValue, callback) => {
    try {
      if (_.isEmpty(inputValue) && !enrollmentId) {
        return callback([]);
      }
      debouncedFetchEnrollments(inputValue, callback);
    } catch (err) {
      console.log(err);
      return callback([]);
    }
  };

  // Auto-load enrollment if enrollmentId is provided
  useEffect(() => {
    if (enrollmentId && show) {
      fetchEnrollments("", (results) => {
        const selected = results.find((opt) => opt.value === enrollmentId);
        if (selected) {
          // Call handleEnrollmentChange to properly set all formik values and state
          setTimeout(() => {
            handleEnrollmentChange(selected);
          }, 0);
        }
      });
    }
  }, [enrollmentId, show]);

  const initialValues = {
    enrollment_id: certificate?.enrollment_id || enrollmentId || "",
    participants: certificate?.participants || [
      {
        participant_id: "",
        certificate_number: "",
        issued_date: "",
        expiry_date: "",
        pdf_file: null,
        verification_code: "",
        course_enrollment_status: false,
      },
    ],
  };

  const createCertificateMutation = useMutation(
    (payload) =>
      sendFormData(
        `${backendUrl}/api/partnership/create-certificate`,

        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Certificate uploaded successfully");
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to upload certificate");
      },
    },
  );

  const updateCertificateMutation = useMutation(
    (payload) =>
      sendFormData(
        `${backendUrl}/api/partnership/certificates/${certificate?.id}`,

        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message || "Certificate updated successfully");
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to update certificate");
      },
    },
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("enrollment_id", values.enrollment_id);

        // Add course_id from selected enrollment
        if (selectedEnrollment?.enrollment?.course_id) {
          formData.append("course_id", selectedEnrollment.enrollment.course_id);
        }

        // Append participants data
        values.participants.forEach((participant, index) => {
          // Build participant object with data (excluding file)
          const participantData = {
            participant_id: participant.participant_id,
            certificate_number: participant.certificate_number,
            issued_date: participant.issued_date,
            ...(participant.expiry_date && {
              expiry_date: participant.expiry_date,
            }),
            ...(participant.verification_code && {
              verification_code: participant.verification_code,
            }),
            ...(participant.course_enrollment_status !== undefined && {
              course_enrollment_status: participant.course_enrollment_status
                ? "Certificate Issued"
                : "Completed",
            }),
          };

          // Stringify and append participant data as JSON
          formData.append(
            `participants${index}`,
            JSON.stringify(participantData),
          );

          // Append PDF file if exists and is a File object (not a string URL)
          if (
            participant.pdf_file &&
            typeof participant.pdf_file !== "string"
          ) {
            formData.append(
              `participants${index}[pdf_file]`,
              participant.pdf_file,
            );
            // Also append file metadata
            formData.append(
              `participants${index}[pdf_file_name]`,
              participant.pdf_file.name,
            );
            formData.append(
              `participants${index}[pdf_file_type]`,
              participant.pdf_file.type,
            );
          }
        });

        if (mode === "create") {
          createCertificateMutation.mutate(formData);
        } else {
          updateCertificateMutation.mutate(formData);
        }
      } catch (error) {
        toast.error("Error processing form");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleEnrollmentChange = (selected) => {
    formik.setFieldValue("enrollment_id", selected?.value || "");
    setSelectedEnrollment(selected);

    // Fetch participants for selected enrollment
    if (
      selected?.enrollment?.participants &&
      selected.enrollment.participants.length > 0
    ) {
      const participantOptions = selected.enrollment.participants.map(
        (participant) => ({
          value: parseInt(participant.id) || participant.id,
          label: `${participant.full_name} (${participant.email})`,
          ...participant,
        }),
      );
      setParticipants(participantOptions);

      // Auto-populate participants array with all participants from the enrollment
      // Filter out participants whose course_enrollment_status is not Completed or Certificate Issued
      const participantsData = selected.enrollment.participants
        .filter(
          (participant) => participant.course_enrollment_status === "Completed",
        )
        .map((participant) => ({
          participant_id: parseInt(participant.id) || participant.id,
          certificate_number: participant.certificate_number || "",
          issued_date: new Date().toISOString(),
          course_enrollment_status: false,
          expiry_date: "",
          pdf_file: null,
          verification_code: "",
        }));
      formik.setFieldValue("participants", participantsData);
    } else {
      // Reset participants if enrollment has no participants
      setParticipants([]);
      formik.setFieldValue("participants", [
        {
          participant_id: "",
          certificate_number: "",
          issued_date: "",
          expiry_date: "",
          pdf_file: null,
          verification_code: "",
        },
      ]);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setSelectedEnrollment(null);
    setParticipants([]);
    onHide();
  };

  const isLoading =
    isSubmitting ||
    createCertificateMutation.isLoading ||
    updateCertificateMutation.isLoading;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as={"h6"}>
          {mode === "create" ? "Upload Certification" : "Update Certification"}
        </Modal.Title>
      </Modal.Header>

      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-4">
              <Form.Label>Select Enrollment *</Form.Label>
              <AsyncSelect
                classNamePrefix="form-select"
                placeholder="Search and select enrollment..."
                name="enrollment_id"
                value={selectedEnrollment}
                onChange={handleEnrollmentChange}
                loadOptions={loadEnrollmentOptions}
                isSearchable={!enrollmentId}
                isClearable={!enrollmentId}
                // isDisabled={!!enrollmentId}
                cacheOptions
                defaultOptions
                menuPlacement="bottom"
              />
              {enrollmentId && (
                <Form.Text className="text-muted">
                  Pre-selected enrollment
                </Form.Text>
              )}
              {formik.touched.enrollment_id && formik.errors.enrollment_id && (
                <Form.Text className="text-danger">
                  {formik.errors.enrollment_id}
                </Form.Text>
              )}
            </Form.Group>

            <hr className="my-4" />

            <div className="mb-4">
              <h6 className="mb-3">
                Participant Certificates ({formik.values.participants.length})
              </h6>

              {formik.values.participants.length === 0 ? (
                <Alert variant="info">
                  Select an enrollment to display participants
                </Alert>
              ) : null}

              <FieldArray
                name="participants"
                render={(arrayHelpers) => (
                  <div>
                    {formik.values.participants.map((participant, index) => (
                      <Card key={index} className="mb-3 border">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>Participant {index + 1}</h6>
                            {formik.values.participants.length > 1 && (
                              <Button
                                variant="link"
                                className="text-danger p-0"
                                onClick={() => arrayHelpers.remove(index)}
                                title="Remove participant"
                              >
                                <DeleteIcon size={18} />
                              </Button>
                            )}
                          </div>

                          <Form.Group className="mb-3">
                            <Form.Label>Participant Name *</Form.Label>
                            <Select
                              classNamePrefix="form-select"
                              placeholder="Select participant"
                              name={`participants[${index}].participant_id`}
                              value={
                                participants.find(
                                  (p) =>
                                    p.value ===
                                    Number(
                                      formik.values.participants[index]
                                        .participant_id,
                                    ),
                                ) || null
                              }
                              onChange={(selected) => {
                                formik.setFieldValue(
                                  `participants[${index}].participant_id`,
                                  selected?.value || "",
                                );
                              }}
                              options={participants}
                              isClearable
                              isInvalid={
                                formik.touched.participants?.[index]
                                  ?.participant_id &&
                                !!formik.errors.participants?.[index]
                                  ?.participant_id
                              }
                            />
                            {formik.touched.participants?.[index]
                              ?.participant_id &&
                              formik.errors.participants?.[index]
                                ?.participant_id && (
                                <Form.Text className="text-danger">
                                  {
                                    formik.errors.participants[index]
                                      .participant_id
                                  }
                                </Form.Text>
                              )}
                          </Form.Group>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Certificate Number *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name={`participants[${index}].certificate_number`}
                                  placeholder="e.g., CERT-2024-001"
                                  value={
                                    formik.values.participants[index]
                                      .certificate_number
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.participants?.[index]
                                      ?.certificate_number &&
                                    !!formik.errors.participants?.[index]
                                      ?.certificate_number
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    formik.errors.participants?.[index]
                                      ?.certificate_number
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Verification Code</Form.Label>
                                <Form.Control
                                  type="text"
                                  name={`participants[${index}].verification_code`}
                                  placeholder="e.g., VERIFY-001"
                                  value={
                                    formik.values.participants[index]
                                      .verification_code || ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Check
                                  type="switch"
                                  id={`participants[${index}].course_enrollment_status`}
                                  name={`participants[${index}].course_enrollment_status`}
                                  label="Issue Certificate"
                                  checked={
                                    formik.values.participants[index]
                                      .course_enrollment_status || false
                                  }
                                  onChange={(e) => {
                                    formik.setFieldValue(
                                      `participants[${index}].course_enrollment_status`,
                                      e.target.checked,
                                    );
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Issued Date *</Form.Label>
                                <Datetime
                                  dateFormat="MMM DD, YYYY"
                                  timeFormat={false}
                                  closeOnSelect={true}
                                  closeOnClickOutside={true}
                                  name={`participants[${index}].issued_date`}
                                  inputProps={{
                                    className: `date-input form-control`,
                                    placeholder: "Select issued date",
                                    readOnly: true,
                                  }}
                                  value={
                                    formik.values.participants[index]
                                      .issued_date
                                  }
                                  onChange={(date) => {
                                    formik.setFieldValue(
                                      `participants[${index}].issued_date`,
                                      date ? new Date(date).toISOString() : "",
                                    );
                                  }}
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `participants[${index}].issued_date`,
                                      true,
                                    )
                                  }
                                />
                                {formik.touched.participants?.[index]
                                  ?.issued_date &&
                                  formik.errors.participants?.[index]
                                    ?.issued_date && (
                                    <Form.Text className="text-danger">
                                      {
                                        formik.errors.participants[index]
                                          .issued_date
                                      }
                                    </Form.Text>
                                  )}
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Expiry Date</Form.Label>
                                <Datetime
                                  dateFormat="MMM DD, YYYY"
                                  timeFormat={false}
                                  closeOnSelect={true}
                                  closeOnClickOutside={true}
                                  name={`participants[${index}].expiry_date`}
                                  inputProps={{
                                    className: `date-input form-control`,
                                    placeholder: "Select expiry date",
                                    readOnly: true,
                                  }}
                                  value={
                                    formik.values.participants[index]
                                      .expiry_date || ""
                                  }
                                  onChange={(date) => {
                                    formik.setFieldValue(
                                      `participants[${index}].expiry_date`,
                                      date ? new Date(date).toISOString() : "",
                                    );
                                  }}
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `participants[${index}].expiry_date`,
                                      true,
                                    )
                                  }
                                />
                                {formik.touched.participants?.[index]
                                  ?.expiry_date &&
                                  formik.errors.participants?.[index]
                                    ?.expiry_date && (
                                    <Form.Text className="text-danger">
                                      {
                                        formik.errors.participants[index]
                                          .expiry_date
                                      }
                                    </Form.Text>
                                  )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label>Upload Certificate PDF</Form.Label>
                            <Form.Control
                              type="file"
                              accept=".pdf"
                              name={`participants[${index}].pdf_file`}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.type !== "application/pdf") {
                                    toast.error(
                                      "Please upload a valid PDF file",
                                    );
                                    return;
                                  }
                                  // if (file.size > 10 * 1024 * 1024) {
                                  //   toast.error(
                                  //     "File size should be less than 10MB",
                                  //   );
                                  //   return;
                                  // }
                                  formik.setFieldValue(
                                    `participants[${index}].pdf_file`,
                                    file,
                                  );
                                }
                              }}
                              onBlur={formik.handleBlur}
                            />
                            {formik.values.participants[index].pdf_file &&
                              typeof formik.values.participants[index]
                                .pdf_file !== "string" && (
                                <Form.Text className="text-success">
                                  {
                                    formik.values.participants[index].pdf_file
                                      .name
                                  }
                                </Form.Text>
                              )}
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    ))}
                    {/* 
                    {selectedEnrollment && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          arrayHelpers.push({
                            participant_id: "",
                            certificate_number: "",
                            issued_date: "",
                            expiry_date: "",
                            pdf_file: null,
                            verification_code: "",
                            course_enrollment_status: false,
                          })
                        }
                        className="mt-2"
                      >
                        + Add Another Participant
                      </Button>
                    )} */}
                  </div>
                )}
              />
            </div>

            {Object.keys(formik.errors).length > 0 && formik.isSubmitting && (
              <Alert variant="danger">
                Please fix the errors above before submitting.
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {mode === "create" ? "Uploading..." : "Updating..."}
                </>
              ) : mode === "create" ? (
                "Upload Certificates"
              ) : (
                "Update Certificates"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
}

export default UploadCertificateModal;
